import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { API_BASE } from '../config';
import './Playground.css';

const Playground = () => {
  const { token, tenant } = useAuth();
  const [mode, setMode] = useState('tokenize_json');
  const [jsonInput, setJsonInput] = useState(`{
  "data": {
    "email": "test@example.com",
    "phone": "+123456789"
  }
}`);
  const [apiKey, setApiKey] = useState('');
  const [tenantUid, setTenantUid] = useState(tenant?.uid || '');
  const [response, setResponse] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showInfo, setShowInfo] = useState(true);
  const [copied, setCopied] = useState(false);
  const [schemas, setSchemas] = useState([]);
  const [selectedSchemaId, setSelectedSchemaId] = useState('');

  const handleCopyResponse = async () => {
    if (!response) return;

    try {
      await navigator.clipboard.writeText(
        JSON.stringify(response, null, 2)
      );
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Copy failed', err);
      alert('Failed to copy response');
    }
  };

  useEffect(() => {
    if (tenant?.uid) {
      setTenantUid(tenant.uid);
    } else {
      const savedTenantUid = localStorage.getItem('vaultize_tenant_uid');
      if (savedTenantUid) setTenantUid(savedTenantUid);
    }

    const savedApiKey = localStorage.getItem('vaultize_api_key');
    if (savedApiKey) {
      setApiKey(savedApiKey);
    } else if (token) {
      fetch(`${API_BASE}/dashboard/api-keys`, {
        headers: { Authorization: `Bearer ${token}` }
      })
      .then(res => res.ok ? res.json() : [])
      .then(keys => {
        if (Array.isArray(keys) && keys.length > 0) {
          const activeKey = keys.find(k => (k.enabled && !k.revoked)) || keys[0];
          const val = activeKey.apiKeyPreview || activeKey.apiKey || activeKey.maskedKey;
          if (val) setApiKey(val);
        }
      })
      .catch(() => {});
    }

    if (token) {
      fetch(`${API_BASE}/admin/schemas`, {
        headers: { Authorization: `Bearer ${token}` }
      })
      .then(res => res.ok ? res.json() : [])
      .then(data => {
        if (Array.isArray(data)) setSchemas(data);
      })
      .catch(() => {});
    }
  }, [tenant, token]);

  const handleSchemaAutoFill = (schemaId) => {
    setSelectedSchemaId(schemaId);
    if (!schemaId) return;

    const schema = schemas.find(s => String(s.id) === String(schemaId));
    if (!schema || !schema.schemaJson) return;

    try {
      const parsed = typeof schema.schemaJson === 'string' ? JSON.parse(schema.schemaJson) : schema.schemaJson;
      const fields = parsed.fields || {};
      const sampleData = {};

      Object.entries(fields).forEach(([fieldName, def]) => {
        const type = (def.piiType || '').toLowerCase();
        if (type === 'card') {
          sampleData[fieldName] = "4532-1234-5678-9014";
        } else if (type === 'ssn') {
          sampleData[fieldName] = "123-45-6789";
        } else if (type === 'email') {
          sampleData[fieldName] = "alice.smith@enterprise.com";
        } else if (type === 'phone') {
          sampleData[fieldName] = "+1-415-555-0199";
        } else if (type === 'ip') {
          sampleData[fieldName] = "192.168.1.100";
        } else if (type === 'name') {
          sampleData[fieldName] = "Alice Smith";
        } else {
          sampleData[fieldName] = "sample_encrypted_value";
        }
      });

      setJsonInput(JSON.stringify({ data: sampleData }, null, 2));
    } catch (err) {
      console.error('Failed to parse schema fields', err);
    }
  };

  const sampleFor = {
    tokenize_fpe: `{
  "piiType": "card",
  "piiValue": "4532-1234-5678-9014",
  "formatPreserving": true,
  "preserveLuhn": true
}`,
    detokenize_single: `{
  "token": "4532-1234-5678-9014"
}`,
    tokenize_json: `{
  "data": {
    "email": "test@example.com",
    "phone": "+123456789"
  }
}`,
    detokenize_json: `{
  "tokenizedData": {
    "email": "token-xyz",
    "phone": "token-abc"
  }
}`
  };

  const apiInfo = {
    tokenize_fpe: {
      url: `${API_BASE}/tokenize`,
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-API-KEY': '<your-api-key>',
        'X-TENANT-ID': '<tenant-uid>',
      },
      request: sampleFor.tokenize_fpe,
      response: `{ "token": "4532-9481-3019-2814", "rootToken": "...", "formatPreserved": true }`
    },
    detokenize_single: {
      url: `${API_BASE}/detokenize`,
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-API-KEY': '<your-api-key>',
        'X-TENANT-ID': '<tenant-uid>',
      },
      request: sampleFor.detokenize_single,
      response: `{ "pii_type": "card", "pii_value": "4532-1234-5678-9014" }`
    },
    tokenize_json: {
      url: `${API_BASE}/tokenize/json`,
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-API-KEY': '<your-api-key>',
        'X-TENANT-ID': '<tenant-uid>',
      },
      request: sampleFor.tokenize_json,
      response: `{ "tokenized": { "email": "token-xyz" } }`
    },
    detokenize_json: {
      url: `${API_BASE}/detokenize/json`,
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-API-KEY': '<your-api-key>',
        'X-TENANT-ID': '<tenant-uid>',
      },
      request: sampleFor.detokenize_json,
      response: `{ "data": { "email": "test@example.com" } }`
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setResponse(null);
    setError('');
    setLoading(true);

    try {
      JSON.parse(jsonInput);
    } catch {
      setError('Invalid JSON input');
      setLoading(false);
      return;
    }

    if (!token && (!apiKey || !tenantUid)) {
      setError('API Key and Tenant UID are required');
      setLoading(false);
      return;
    }

    const headers = {
      'Content-Type': 'application/json',
    };

    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    if (apiKey) {
      headers['X-API-KEY'] = apiKey;
      localStorage.setItem('vaultize_api_key', apiKey);
    }
    if (tenantUid) {
      headers['X-TENANT-ID'] = tenantUid;
      localStorage.setItem('vaultize_tenant_uid', tenantUid);
    }

    try {
      const res = await fetch(apiInfo[mode].url, {
        method: apiInfo[mode].method,
        headers,
        body: jsonInput,
      });

      if (!res.ok) throw new Error(`${res.status}: ${res.statusText}`);
      setResponse(await res.json());
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="playground-page">
      <div className="container">
        <div className="playground-card">

          <h1 className="playground-title">API Playground</h1>
          <p className="playground-subtitle">
            Test Vaultize tokenization and detokenization APIs
          </p>

          <div className="tab-buttons">
            {Object.keys(sampleFor).map((m) => (
              <button
                key={m}
                className={mode === m ? 'active' : ''}
                onClick={() => {
                  setMode(m);
                  setJsonInput(sampleFor[m]);
                  setResponse(null);
                  setError('');
                  setSelectedSchemaId('');
                }}
              >
                {m.replace('_', ' ')}
              </button>
            ))}
          </div>

          {mode === 'tokenize_json' && schemas.length > 0 && (
            <div className="schema-autofill-bar">
              <span className="autofill-label">⚡ Auto-Fill Payload from Registered Schema:</span>
              <select
                className="autofill-select"
                value={selectedSchemaId}
                onChange={(e) => handleSchemaAutoFill(e.target.value)}
              >
                <option value="">Select a registered schema…</option>
                {schemas.map(s => (
                  <option key={s.id} value={s.id}>{s.name} ({s.description || 'Custom Schema'})</option>
                ))}
              </select>
            </div>
          )}

          <div className="input-headers">
            <input
              type="text"
              placeholder="API Key (X-API-KEY)"
              value={apiKey}
              onChange={(e) => setApiKey(e.target.value)}
            />
            <input
              type="text"
              placeholder="Tenant UID (X-TENANT-ID)"
              value={tenantUid}
              onChange={(e) => setTenantUid(e.target.value)}
            />
          </div>

          <button className="info-toggle" onClick={() => setShowInfo(!showInfo)}>
            {showInfo ? 'Hide API Info' : 'Show API Info'}
          </button>

          {showInfo && (
            <div className="api-info">
              <pre>{apiInfo[mode].request}</pre>
            </div>
          )}

          {error && <div className="playground-error">{error}</div>}

          <form onSubmit={handleSubmit} className="playground-form">
            <textarea
              value={jsonInput}
              onChange={(e) => setJsonInput(e.target.value)}
            />
            <button
              type="submit"
              className="btn btn-primary btn-large"
              disabled={loading}
            >
              {loading ? 'Sending…' : 'Send Request'}
            </button>
          </form>

          <div className="response-section">
            <div className="response-header"></div>
            <h3>Response</h3>
            <button
              className="copy-btn"
              onClick={handleCopyResponse}
              disabled={!response}
            >
                    {copied ? 'Copied ✓' : 'Copy'}
            </button>
           </div>
          {response && (
            <pre>{JSON.stringify(response, null, 2)}</pre>
            )}
        </div>
      </div>
    </section>
  );
};

export default Playground;
